<script setup>
import { reactive, ref } from 'vue';
import { Form } from 'vee-validate';
import FormInputCheckbox from '../fields/FormInputCheckbox.vue';
import * as yup from 'yup';
import { checkboxValidation } from '../utils/validations';

const emit = defineEmits(['advance-step']);

const props = defineProps({
    formData: {
        type: Object,
        required: true
    },
    insurer: {
        type: Object,
        required: true
    }
});

const fields = reactive({
    acceptMedicalConsent1: false,
    acceptMedicalConsent2: false,
    ...props.formData
});

const submit = (values) => {
    console.log(values);
    emit('advance-step', values);
};

const step = ref(0);
const schema = yup.object({
    acceptMedicalConsent1: checkboxValidation,
    acceptMedicalConsent2: checkboxValidation
});

const advanceStep = () => {
    step.value = 1;
    window.scrollTo({ top: 0, behavior: 'smooth' });
};
</script>
<template>
    <Form :validation-schema="schema" @submit="submit">
        <h2 class="panel-heading h2">Medical Consent Authority</h2>

        <template v-if="step === 0">
            <div class="form-panel">
                <p class="tw-font-bold">Important Information</p>
                <p class="tw-underline">Notes on releasing information about your health</p>
                <p>
                    Your health information includes details about all your interactions with health
                    providers, and may include details such as your symptoms, treatment,
                    consultations, personal medical history and lifestyle. Health providers cannot
                    release this information about you without your consent.
                </p>
                <p>
                    <span class="tw-font-bold">TAL Life Limited (TAL)</span>, collect and use your
                    health information to assess your application for cover, to assess and manage
                    your claim, or to confirm the information you gave us when you applied for cover
                    or made a claim. This is why TAL need your consent.
                </p>
                <p>
                    Each time you apply for cover or make a claim, TAL will ask you for a fresh
                    consent. TAL will respect your privacy by only asking for the information TAL
                    reasonably need, and TAL will tell you each time they use your consent.
                </p>
                <p>
                    Even if TAL collect information from health providers (such as your General
                    Practitioner), before the insurance starts you must still tell them every matter
                    (including about your health) that is relevant to their decision about whether
                    to offer you insurance, and if so, on what terms. This is your Duty to take
                    reasonable care not to make a misrepresentation under the Insurance Contracts
                    Act 1984 (Cth).
                </p>
                <p>Please read each Authority carefully and the explanatory notes below.</p>
                <p>
                    <span class="tw-font-bold">Authority 1 explanatory notes </span> – through this
                    Authority, with the exception of a copy of the consultation notes held by your
                    General Practitioner/Practice, you are consenting to any health provider
                    releasing any health information about you in the form TAL ask for. This may
                    involve, for example:
                </p>
                <ul>
                    <li>preparing a general report and/or a report about a specific condition;</li>
                    <li>accessing and releasing your records in SafeScript;</li>
                    <li>releasing your hospital patient notes;</li>
                    <li>releasing the results of any investigations they have done; and/or</li>
                    <li>releasing correspondence with other health providers.</li>
                </ul>
                <p>
                    <span class="tw-font-bold">Authority 2 explanatory notes</span> – through this
                    Authority, you are consenting to any General Practitioner/Practice you have
                    attended releasing a copy of your full record, including consultation notes, but
                    only if TAL have asked them to provide a general report and/or a report about a
                    specific condition under Authority 1, and either:
                </p>
                <ul>
                    <li>
                        they will be unable to, or did not, provide the report within 4 weeks; or
                    </li>
                    <li>
                        the report provided is incomplete, or contains inconsistencies or
                        inaccuracies.
                    </li>
                </ul>
                <p>
                    Your General Practitioner maintains consultation notes to support quality care,
                    your wellbeing and to meet legal and professional requirements. General
                    Practitioners/Practices should only release a copy of your full record,
                    including consultation notes, for life insurance purposes in the rare
                    circumstances set out above.
                </p>
                <p>
                    If you choose to withhold your consent to this authority, TAL may not be able to
                    process your application for cover or a claim.
                </p>
            </div>

            <button class="btn btn--green btn--full" @click.prevent="advanceStep">Next</button>
        </template>

        <template v-else>
            <div class="form-panel">
                <h3 class="tw-mb-6 h3">Medical Consent Authority 1</h3>

                <div class="wysiwyg-output">
                    <p class="tw-underline">
                        Authority 1 – to release any of my health information except the
                        consultation notes held by my General Practitioner/Practice
                    </p>
                    <p>
                        With the exception of consultation notes held by any General
                        Practitioner/Practice I have attended, I authorise any health provider,
                        practitioner, practice, psychologist, dentist, allied health services
                        provider or any hospital to access and release, in writing or verbally, any
                        details of my health information to TAL Life Limited (TAL), or to third
                        parties they engage.
                    </p>
                    <p>I agree to all the following:</p>
                    <ul>
                        <li>
                            My health information can be released in the form TAL Life Limited (TAL)
                            asks for, such as a general report, a report about a specific condition,
                            my records in SafeScript, any hospital notes, or correspondence between
                            health providers.
                        </li>
                        <li>
                            TAL Life Limited (TAL) can collect, use, store and disclose my personal
                            information (including sensitive information) in accordance with privacy
                            laws and Australian Privacy Principles.
                        </li>
                        <li>
                            This Authority is valid only while TAL Life Limited (TAL) is assessing
                            my claim or application for cover, or is verifying disclosures I made in
                            connection with the cover.
                        </li>
                        <li>
                            A copy or transcript of this Authority will be valid and effective, and
                            this Authority should be accepted as valid and effective where I have
                            signed electronically or consented verbally.
                        </li>
                    </ul>
                </div>

                <FormInputCheckbox
                    class="tw-mt-6 sm:tw-mt-10"
                    name="acceptMedicalConsent1"
                    label="I, the Life Insured confirm I have read and agree to Medical Consent Authority 1.*"
                    :value="fields.acceptMedicalConsent1"
                />

                <h3 class="tw-mt-16 tw-mb-6 h3">Medical Consent Authority 2</h3>

                <div class="wysiwyg-output">
                    <p class="tw-underline">
                        Authority 2 – to release a copy of the full record, including consultation
                        notes, held by my General Practitioner/Practice in specified circumstances
                    </p>
                    <p>
                        I authorise any General Practitioner/Practice I have attended to release a
                        copy of my full record, including consultation notes, to TAL Life Limited
                        (TAL), or to third parties they engage, only if TAL Life Limited (TAL) has
                        asked them for a report on my health and either:
                    </p>
                    <ul>
                        <li>
                            the General Practitioner/Practice will be unable to, or did not, provide
                            the report within four weeks; or
                        </li>
                        <li>
                            the report is incomplete, or contains inconsistencies or inaccuracies.
                        </li>
                    </ul>
                    <p>I agree to all the following:</p>
                    <p>
                        TAL Life Limited (TAL) can collect, use, store and disclose my personal
                        information (including sensitive information) in accordance with privacy
                        laws and Australian Privacy Principles.
                    </p>
                    <ul>
                        <li>
                            This Authority is valid only while TAL Life Limited (TAL) is assessing
                            my claim or application for cover, or is verifying disclosures I made in
                            connection with the cover.
                        </li>
                        <li>
                            A copy or transcript of this Authority will be valid and effective, and
                            this Authority should be accepted as valid and effective where I have
                            signed electronically or consented verbally.
                        </li>
                    </ul>
                </div>

                <FormInputCheckbox
                    class="tw-mt-6 sm:tw-mt-10"
                    name="acceptMedicalConsent2"
                    label="I, the Life Insured confirm I have read and agree to Medical Consent Authority 2.*"
                    :value="fields.acceptMedicalConsent2"
                />
            </div>

            <button class="btn btn--green btn--full">Next</button>
        </template>
    </Form>
</template>
